<script setup lang="ts" generic="Key extends string">
import {
  ref, shallowRef, computed,
} from 'vue';
import {
  Button, Dropdown, Menu, MenuItem,
} from 'ant-design-vue';
import type { FontAwesomeIconProps } from '@fortawesome/vue-fontawesome';

import type { UiSearchBaseInputEntry, UiSearchValues } from './types';
import SearchRow from './SearchRow.vue';

const props = defineProps<{
  inputs: UiSearchBaseInputEntry<Key>[];
}>();

const values = defineModel<UiSearchValues<Key>>('values', { required: true });

const filterValueIsSet = (value: unknown): boolean => {
  if (value === undefined || value === null) {
    return false;
  }
  if (Array.isArray(value)) {
    return value.length > 0;
  }
  if (typeof value === 'string') {
    return value.trim().length > 0;
  }
  return true;
};

interface EntryMeta {
  name: Key;
  label: string;
  icon: FontAwesomeIconProps['icon'];
  alwaysOn: boolean;
}

const toLoose = (entry: UiSearchBaseInputEntry<Key>) => entry as unknown as {
  name: Key;
  field?: { label?: string };
  icon?: FontAwesomeIconProps['icon'];
  alwaysOn?: boolean;
};

const meta = computed<EntryMeta[]>(() => props.inputs.map((entry) => {
  const loose = toLoose(entry);
  return {
    name: loose.name,
    label: loose.field?.label ?? 'Unknown',
    icon: loose.icon ?? ['fal', 'question'],
    alwaysOn: !!loose.alwaysOn,
  };
}));

const visibilityByKey = ref<Record<string, boolean>>(
  Object.fromEntries(
    props.inputs
      .filter((entry) => filterValueIsSet(values.value[toLoose(entry).name]))
      .map((entry) => [toLoose(entry).name, true]),
  ),
);

const filterAddOrder = shallowRef<Key[]>([]);

const isVisible = (name: Key): boolean => {
  const found = meta.value.find((entry) => entry.name === name);
  const defaultVisible = !!found?.alwaysOn;
  return name in visibilityByKey.value ? !!visibilityByKey.value[name] : defaultVisible;
};

const orderedInputKeys = computed<Key[]>(() => {
  const alwaysOnKeys: Key[] = [];
  const optionalVisible: Key[] = [];
  for (const entry of meta.value) {
    if (!isVisible(entry.name)) {
      continue;
    }
    if (entry.alwaysOn) {
      alwaysOnKeys.push(entry.name);
    } else {
      optionalVisible.push(entry.name);
    }
  }
  const stack = filterAddOrder.value;
  const notInStack = optionalVisible.filter((key) => !stack.includes(key));
  const inStack = stack.filter((key) => optionalVisible.includes(key));
  return [...alwaysOnKeys, ...notInStack, ...inStack];
});

const availableFields = computed<EntryMeta[]>(
  () => meta.value.filter((entry) => !isVisible(entry.name)),
);

const hasClearableFilters = computed<boolean>(
  () => meta.value.some((entry) => isVisible(entry.name) && !entry.alwaysOn),
);

const entryByName = (name: Key): UiSearchBaseInputEntry<Key> => {
  const entry = props.inputs.find((item) => toLoose(item).name === name);
  if (!entry) {
    throw new Error(`Search: no input configured for key "${name}"`);
  }
  return entry;
};

const rowClass = (name: Key): string => {
  const found = meta.value.find((entry) => entry.name === name);
  return found?.alwaysOn
    ? 'w-full min-w-0 @xl:basis-full'
    : 'w-full min-w-0 @xl:flex-[1_1_calc(50%-0.5rem)]';
};

const onSelect = (name: Key): void => {
  visibilityByKey.value[name] = true;
  filterAddOrder.value = [...filterAddOrder.value.filter((key) => key !== name), name];
};

const onHideField = (name: Key): void => {
  const found = meta.value.find((entry) => entry.name === name);
  if (found?.alwaysOn) {
    return;
  }
  visibilityByKey.value[name] = false;
  filterAddOrder.value = filterAddOrder.value.filter((key) => key !== name);
  values.value[name] = undefined;
};

const onClearAll = (): void => {
  for (const entry of meta.value) {
    if (!entry.alwaysOn) {
      visibilityByKey.value[entry.name] = false;
      values.value[entry.name] = undefined;
    }
  }
  filterAddOrder.value = [];
};

const onAddMenuClick = (info: { key: string | number }): void => {
  onSelect(info.key as Key);
};
</script>

<template>
  <div class="flex w-full min-w-0 flex-col gap-3">
    <div class="w-full min-w-0 @container">
      <div class="flex w-full min-w-0 flex-col gap-3 @xl:flex-row @xl:flex-wrap @xl:items-stretch @xl:gap-2">
        <SearchRow
          v-for="name in orderedInputKeys"
          :key="name"
          :entry="entryByName(name)"
          :row-class="rowClass(name)"
          :value="values[name]"
          :operator="(values[`${name}Operator`] as string | undefined)"
          @update:value="(value) => { values[name] = value; }"
          @update:operator="(value) => { values[`${name}Operator`] = value; }"
          @remove="onHideField(name)"
        >
          <template #[`${name}-input`]="slotProps">
            <slot :name="`${name}-input`" v-bind="slotProps" />
          </template>
        </SearchRow>
      </div>
    </div>
    <div class="flex flex-wrap items-center gap-2">
      <Dropdown v-if="availableFields.length > 0" :trigger="['click']">
        <Button>
          <FaIcon :icon="['fal', 'plus']" class="mr-1" />
          Add filter
        </Button>
        <template #overlay>
          <Menu @click="onAddMenuClick">
            <MenuItem v-for="field in availableFields" :key="field.name">
              <FaIcon :icon="field.icon" class="mr-2" />
              {{ field.label }}
            </MenuItem>
          </Menu>
        </template>
      </Dropdown>
      <Button v-if="hasClearableFilters" @click="onClearAll">
        Clear all
      </Button>
    </div>
  </div>
</template>
